import React from 'react';
import { SectionFolder } from '@src/components/SectionFolder';
import { SectionTitle } from '@src/components/SectionTitle';
import { SectionProps } from '../types/Settings';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { FORM_VALUE_TITLE_SIZE, SECTION_TITLE_SIZE } from '../utils/variables';
import { SectionContent } from '../subcomponents/SectionContent';
import { createChangeSettings } from '../utils/createChangeSettings';
import { SettingsCheckbox } from '../subcomponents/SettingsCheckbox';
import { WidgetView } from 'common';
import { ProductDisplayIcon } from '../../Icons/DropDownIcons/ProductDisplay';
import { SubSectionContent } from '../subcomponents/SubSectionContent';
import { FormLine } from '@src/components/Form/FormLine';
import { Input } from '@src/components/Input';
import { style } from 'typestyle';
import { Tooltip } from '@mui/material';
import { InfoSvgIcon } from '@src/components/SvgIcons/Info';
import { ADALONG_COLORS } from '@src/styles';

const ProductDisplay = ({ opened, onOpen, editedWidget, setEditedWidget }: SectionProps) => {
  const changeSettings = createChangeSettings(editedWidget, setEditedWidget);

  function updateMaxExactProductsDisplayed(targetValue: HTMLInputElement['value']): void {
    const newValue = parseInt(targetValue, 10);
    changeSettings({
      max_exact_products_displayed: newValue > 0 ? newValue : null,
    });
  }

  function updateMaxSimilarProductsDisplayed(targetValue: HTMLInputElement['value']): void {
    const newValue = parseInt(targetValue, 10);
    changeSettings({
      max_similar_products_displayed: newValue > 0 ? newValue : null,
    });
  }

  function updateShopThisLookText(value: string): void {
    changeSettings({ shop_this_look_text: value || undefined });
  }

  function updateMoreLikeThisText(value: string): void {
    changeSettings({ more_like_this_text: value || undefined });
  }

  // uncheck a value when its counterpart is checked
  const handleCheckboxClick = (
    checkedKey: keyof WidgetView['settings'],
    newValues: Partial<WidgetView['settings']>,
  ) => {
    const newSettings: Partial<WidgetView['settings']> = {
      [checkedKey]: !editedWidget.settings[checkedKey],
      ...newValues,
    };
  
    changeSettings(newSettings);
  };

  return (
    <SectionFolder
      title={
        <SectionTitle size={SECTION_TITLE_SIZE}>
          <ProductDisplayIcon />
          {lang.title}
        </SectionTitle>
      }
      opened={opened}
      afterTitle={
        <Tooltip title={lang.tooltips.productDisplay} placement="top">
          <span className={infoProductDisplayClass}>
            <InfoSvgIcon />
          </span>
        </Tooltip>
      }
      onOpen={onOpen}
    >
      <SectionContent>
        <SubSectionContent>
          <p className={logicalConnectorClass}> {lang.fields.exactDisplayed.toUpperCase()} </p>

          <div className={textFieldClass}>
            <FormLine title={lang.fields.shopThisLookText} size={FORM_VALUE_TITLE_SIZE}>
              <Input
                type='text'
                onChange={({ target }) => updateShopThisLookText(target.value)}
                value={editedWidget.settings.shop_this_look_text || ''}
                placeholder={lang.fields.shopThisLookPlaceholder}
                className={textInputClass}
              />
            </FormLine>
          </div>
          
          <div className={maxInputRowClass}>
            <span className={maxLabelClass}>{lang.fields.max}</span>
            <Input
              type='number'
              onChange={({ target }) => updateMaxExactProductsDisplayed(target.value)}
              value={editedWidget.settings.max_exact_products_displayed || ''}
              placeholder={lang.fields.maxPlaceholder}
              title={lang.fields.maxPlaceholder}
              className={maxInputClass}
            />
          </div>

          <SettingsCheckbox
            label={lang.fields.displayExactOnly}
            checked={editedWidget.settings.display_exact_products_only}
            onClick={() =>
              handleCheckboxClick('display_exact_products_only', {
                display_similar_products_only: false,
              })
            }
          />

          <p className={similarSectionClass}> {lang.fields.similarDisplayed.toUpperCase()} </p>

          <div className={textFieldClass}>
            <FormLine title={lang.fields.moreLikeThisText} size={FORM_VALUE_TITLE_SIZE}>
              <Input
                type='text'
                onChange={({ target }) => updateMoreLikeThisText(target.value)}
                value={editedWidget.settings.more_like_this_text || ''}
                placeholder={lang.fields.moreLikeThisPlaceholder}
                className={textInputClass}
              />
            </FormLine>
          </div>

          <div className={maxInputRowClass}>
            <span className={maxLabelClass}>{lang.fields.max}</span>
            <Input
              type='number'
              onChange={({ target }) => updateMaxSimilarProductsDisplayed(target.value)}
              value={editedWidget.settings.max_similar_products_displayed || ''}
              placeholder={lang.fields.maxPlaceholder}
              title={lang.fields.maxPlaceholder}
              className={maxInputClass}
            />
          </div>

          <SettingsCheckbox
            label={lang.fields.displaySimilarOnly}
            checked={editedWidget.settings.display_similar_products_only}
            onClick={() =>
              handleCheckboxClick('display_similar_products_only', {
                display_exact_products_only: false,
              })
            }
          />

        </SubSectionContent>
      </SectionContent>
    </SectionFolder>
  );
};

const lang = LocalizedStrings({
  en: {
    title: 'Product Display',
    tooltips: {
      productDisplay: 'Control how many products are shown and whether to display only exact or similar product matches',
    },
    fields: {
      displayExactOnly: 'Show EXACT products only',
      displaySimilarOnly: 'Show SIMILAR products only',
      exactDisplayed: 'Exact products displayed',
      similarDisplayed: 'Similar products displayed',
      max: 'Max',
      maxPlaceholder: 'Infinite',
      shopThisLookText: 'Shop this look title',
      moreLikeThisText: 'More like this title',
      shopThisLookPlaceholder: 'Your replacement title',
      moreLikeThisPlaceholder: 'Your replacement title',
    },
  },
});

const infoProductDisplayClass = style({
  marginLeft: 10,
  color: ADALONG_COLORS.LIGHT_GRAY,
});

const logicalConnectorClass = style({
  fontSize: 12,
  fontWeight: 'bold',
});

const similarSectionClass = style({
  fontSize: 12,
  fontWeight: 'bold',
  marginTop: 30,
});

const maxInputRowClass = style({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '4px',
});

const maxLabelClass = style({
  fontSize: 11,
  textTransform: 'uppercase',
  color: ADALONG_COLORS.LIGHT_GRAY,
  whiteSpace: 'nowrap',
});

const textFieldClass = style({
  marginTop: 12,
});

const maxInputClass = style({
  width: '50px',
});

const textInputClass = style({
  width: '200px',
  marginRight: '10px',
});

export default ProductDisplay;
